import { get } from 'svelte/store';
import { format } from '../../services/i18n';
import { devices_status, devices_settings } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
import { rooms } from 'src/stores/lists/rooms';
import { alarms } from 'src/stores/lists/scenes-derived';
import { scenes } from 'src/stores/lists/scenes';
import type {
	EventsLogData,
	EventsLogRequest,
	EventsLogResponse,
	EventsLogResultData,
} from '../../types/requests/eventslog/events_log';
import { capFirstChar, rgbToCss, sortOn } from '../../helpers/utilities';
import {
	celsius_to_fahrenheit,
	isBluTRV,
	isBreaker,
	isEM,
	isPill,
	real_id,
	tmp_units_from_settings,
} from '../../helpers/interface_helpers';
import { isPeripheralDevice, getSwitchPeripheralsTags } from 'src/helpers/addons/addon_device_helpers';
import { isVirtualDevice } from 'src/helpers/virtual_helpers/virtual_components_helpers';
import { isProSensorAddon } from 'src/helpers/addons/addon_support_helpers';
import type { TemplateComponent, VirtualWebIcons } from 'src/types/VirtualComponents';
import {
	getStatusIconByRole,
	getTemplateComponents,
	isXT1Module,
	translateTemplateTitle,
} from 'src/helpers/xt1_helpers/template_helpers';
import { getWebIconStatuses } from 'src/helpers/virtual_helpers/virtual_icons_helpers';
import { bthomeObjectInfos } from 'src/helpers/virtual_helpers/virtual_bthome_helper';
import type { Device } from '../../types/Device';
import { postReq } from 'src/services/http';
import { TEMPERATURE_SENSORS_IN_CELSIUS } from 'src/constants';
import { getIlluminationIcon } from 'src/helpers/device_data_helpers';
import { getThePillEventLogTags } from 'src/helpers/thepill/thepill_data_helpers';
import { convertAndFormatDistance, DISTANCE_BASE_SIZE } from 'src/helpers/distance_helper';
import { getCameraZone } from '../Camera/helpers/camera-zone-helpers';

export const parseEventLogData = (data: EventsLogResponse, deviceChannel: number = 0) => {
	//ToDo need to handle errors
	const result: EventsLogData[] = [],
		allDevices = get(devices),
		allRooms = get(rooms),
		allSettings = get(devices_settings),
		allScene = get(scenes),
		allAlarms = get(alarms);

	for (const key in data?.result) {
		const value: EventsLogResultData[] = data.result[key];
		const len = value.length;

		if (len > 0) {
			for (let i = 0; i < len; ++i) {
				let icon = null;
				let deviceID = value[i].tag;

				const eventID = value[i].e;
				const config = EVENT_KEYS[eventID];

				if (eventID != 17 && deviceChannel >= 200) {
					continue;
				}
				if (eventID == 6) {
					let event;
					let category;

					if (allAlarms[deviceID]) {
						// alarms are category scene by default
						// so we have to check them firstly in allAlarms
						event = allAlarms[deviceID];
						category = 'alarm';
					} else if (allScene[deviceID]) {
						event = allScene[deviceID];
						category = 'scene';
					} else {
						continue;
					}

					const roomID = event._meta.room,
						room = allRooms[roomID]?.name || 'DASHBOARD',
						name = event._meta.name;

					icon = categoryIcon(category);

					result.push({
						eventID,
						timestamp: value[i].t,
						catIcon: icon,
						roomID,
						name,
						data: {},
						room,
						category,
						icon: { icon, style: '' },
						id: key,
						sceneId: deviceID,
					});
					continue;
				}

				if (typeof config === 'undefined') {
					console.error(`error for device ID: ${deviceID}, unknown event ID: ${eventID}`);
					continue;
				}

				const parsedPayload =
					typeof value[i].p === 'string' ? JSON.parse(value[i].p) : value[i].p;
				const values = parsedPayload.splice(1);

				const channel = config?.includes('channel') ? values[0] : 0;

				if (channel == 100) {
					deviceID = `${deviceID?.split('_')?.[0]}`;
				} else if (channel && allDevices[`${deviceID}_${channel}`]) {
					deviceID = `${deviceID}_${channel}`;
				}

				let device: Device = allDevices[deviceID];

				if (!device && eventID === 23) {
					const channelDeviceId =
						deviceChannel > 0 ? `${deviceID}_${deviceChannel}` : deviceID;
					device = allDevices[channelDeviceId] || allDevices[deviceID];
					if (device) {
						deviceID = device.id;
					}
				}

				if (values.includes(null)) {
					// console.log('what devices have null values?');
					// console.log('device', device);
					// console.log('values', values);
					// console.log('config', config);
				}

				// Check if main device exists
				// E.g. thePill has tags for every component
				if (!device && channel >= 200) {
					let tryMainDeviceID = deviceID.split('_')[0];
					const tryMainDevice = allDevices[tryMainDeviceID];

					if (!isPill(tryMainDevice?.type)) continue;

					device = allDevices[tryMainDeviceID];
					deviceID = tryMainDeviceID;
				}

				if (!device || channel < 0 || values.includes('?254') || values.includes('LL')) {
					//null is a extra complicated event which is better to be hidden
					continue;
				}

				if (eventID == 20) {
					//TODO (R.K.): Figure ig we will neded something from this at later stage
					// console.log('Track event', deviceID, parsedPayload, value[i]);
					// console.log('@!@$!@$!@$!@$!@$@!$', parsedPayload,value)
				}

				const name = device.name;
				const roomID = device.room_id;
				const room = allRooms[roomID]?.name || 'DASHBOARD';
				const type = device.type;

				if (eventID == 17) {
					const data = {};
					let val = values[1];
					let event = '';
					let icon = null;
					let subIcon = null;

					// Special handling for S4SN-0U61X radar device
					if (device.type === 'S4SN-0U61X') {
						const component = values[0]; // e.g., "presencezone:202"
						// console.log('Processing radar presence event', deviceID, component, val);

						if (component.startsWith('presencezone:')) {
							const zoneNumber = component.split(':')[1];

							if (val === true) {
								event = get(format)('presence');
								icon = {
									icon: 'fas fa-eye',
									style: 'color: var(--color-success);',
								};
							} else if (val === false) {
								event = get(format)('no_presence');
								icon = {
									icon: 'far fa-eye-slash',
									style: 'color: var(--color-gray);',
								};
							}

							data['module'] = {
								name: `${get(format)('presence_zone')} ${zoneNumber}: ${event}`,
								value: event,
								icon: subIcon,
							};

							// console.log('Adding radar event to result:', { event, data, icon });

							result.push({
								eventID,
								timestamp: value[i].t,
								catIcon: '',
								roomID,
								name: `${name} ${event}`,
								data,
								room,
								category: 'modal',
								icon: icon || { icon: '', style: '' },
								id: key,
							});
							continue;
						}
					}

					if (isXT1Module(device.type)) {
						const component: TemplateComponent = getTemplateComponents(
							device,
							RegExp(values[0]),
						)?.[0];
						const role = component?.role || '';
						const unit = component?.config?.meta?.ui?.unit || '';
						if (!component) continue;
						if (typeof val == 'boolean') {
							if (role.startsWith('zone')) {
								subIcon = `fa${val ? 's' : 'r'} fa-droplet`;
							}
							icon = eventLogIcon('turn', val, type);
							val = val ? 'on' : 'off';
							event =
								role == 'state'
									? 'status_update'
									: translateTemplateTitle(component?.config?.name || role);
						} else if (role == 'position') {
							icon = {
								icon: `fa${val > 0 ? 's' : 'r'} fa-pipe-valve`,
								style: '',
							};
							event = ` ${translateTemplateTitle(role)}: ${val} ${unit}`;
						} else if (role == 'last_precipitation') {
							icon = { icon: getStatusIconByRole(role), style: '' };
							subIcon = 'fas fa-raindrops';
							event = `${val} ${unit}`;
						} else if (role == 'fan_speed') {
							event = ` ${translateTemplateTitle(role)}: ${get(format)(`label_fan_mode_${val}`)}`;
							const statusIcon = getStatusIconByRole(role);
							if (statusIcon) {
								icon = { icon: statusIcon, style: '' };
								subIcon = statusIcon;
							}
						} else {
							event = ` ${translateTemplateTitle(role)}: ${val} ${unit}`;
							const statusIcon = getStatusIconByRole(role);
							if (statusIcon) {
								icon = { icon: statusIcon, style: '' };
								subIcon = statusIcon;
							}
						}
					} else {
						const component = values[0];
						const status = devices_status.getByRealId(deviceID);
						const setting = allSettings[real_id(deviceID)]?.[component];
						if (
							deviceChannel < 200 ||
							(deviceChannel >= 200 &&
								deviceChannel < 1200 &&
								!status?.[`group:${deviceChannel}`]?.value?.includes(component)) ||
							(deviceChannel >= 1200 &&
								setting?.addr !=
									allSettings[real_id(deviceID)]?.[
										`bthomedevice:${deviceChannel - 1000}`
									]?.addr)
						) {
							continue;
						}
						if (/enum:\d{3}$/.test(component) && setting?.meta?.ui?.titles?.[val]) {
							val = setting.meta.ui.titles[val];
						}
						event = ` ${translateTemplateTitle(
							setting?.name || component,
						)}: ${val} ${setting?.meta?.ui?.unit || ''}`;
						if (deviceChannel >= 1200 && deviceChannel < 2200) {
							const bthomeObj = bthomeObjectInfos.find((i) => i.id == setting?.obj_id);
							if (bthomeObj) {
								event = ` ${translateTemplateTitle(
									bthomeObj?.name || component,
								)}: ${val} ${bthomeObj.unit}`;
								const webIconStatuses: VirtualWebIcons[] = getWebIconStatuses();
								icon = webIconStatuses.find(
									(i) => i.bthome_obj && i.bthome_obj.includes(bthomeObj.name),
								);
								event = icon
									? `${val} ${bthomeObj.unit}`
									: ` ${translateTemplateTitle(
											bthomeObj?.name || component,
										)}: ${val} ${bthomeObj.unit}`;
							}
						} else if (setting?.meta?.ui?.webIcon) {
							const webIconStatuses: VirtualWebIcons[] = getWebIconStatuses();
							icon = webIconStatuses[setting.meta.ui.webIcon];
						}
					}
					data['module'] = {
						name: event,
						value: event,
						icon: subIcon,
					};
					result.push({
						eventID,
						timestamp: value[i].t,
						catIcon: '',
						roomID,
						name: event,
						data,
						room,
						category: 'modal',
						icon: icon || { icon: '', style: '' },
						id: key,
					});
					continue;
				}

				const category = device.category === 'sensor' ? 'sensor' : 'actuator';
				const isInputsReader =
					device.category === 'inputs_reader' ||
					(device.category === 'module' && device.mode === 'inputs_reader');
				const units =
					category == 'sensor' ? tmp_units_from_settings(allSettings[real_id(device.id)]) : '';

				const data = values.reduce((obj, value, index, array) => {
					if (value != null) {
						let _key = config[index];

						if (COVER_CRITICAL_ERRORS.includes(value)) _key = value;

						// Skip only this payload field when firmware sends the literal "undefined".
						if (typeof value === 'string' && value === 'undefined') {
							return obj;
						}

						if (device.type === 'S4SN-0071A' || device.type === 'S4SN-0071Z') {
							//flood gen 4
							if (_key == 'alarm_hum') {
								_key = 'water';
							}

							if (value === 'ulp') {
								//ulp is a ultra low power processor aka cable events. Iliya said we can ignore them
								_key = '';
							}
						}

						if (
							device.type === 'SNSN-0031Z' ||
							device.type === 'S4SN-1031Z' ||
							device.type === 'S4SN-0031Z' ||
							device.type === 'S4SN-0071A' || //flood gen 4
							device.type === 'S4SN-0071Z' || //flood S gen 4
							device.type === 'SMSN-0031ZL' || //Smoke LTE
							device.type === 'S3SN-0082A' //Alarm bridge
						) {
							if (_key === 'temperature') {
								_key = 'battery'; //need to do better solution
							}
						}

						// Handle S4SN-0U61X (Shelly Presence) radar events
						if (device.type === 'S4SN-0U61X') {
							// Map radar tracking events to user-friendly descriptions
							if (_key === 'track') {
								_key = 'presence';
								value = 'detected';
							} else if (_key === 'no_track') {
								_key = 'presence';
								value = 'cleared';
							} else if (value && typeof value === 'string') {
								// Handle zone events
								if (value === 'zone_active') {
									_key = 'presence_zone';
									value = 'activated';
								} else if (value === 'zone_inactive') {
									_key = 'presence_zone';
									value = 'deactivated';
								}
								// Handle illumination events
								else if (value.startsWith('illumination_')) {
									_key = 'illumination';
									value = value.replace('illumination_', '');
								}
							}
						}

						// Handle Camera Zones
						if (_key === 'camerazone') {
							obj[_key] = handleCameraZoneEvent(allSettings, device, value, _key);
							return obj;
						}

						if (_key === 'imageId' || _key === 'recordingId') {
							obj[_key] = { name: _key, value, hidden: true };
							return obj;
						}

						if (eventID === 23 && _key === 'reason' && typeof value === 'string') {
							value = translateBreakerEventSource(value);
						}

						if (_key && _key !== 'channel') {
							// handle inputs readers
							if (isInputsReader && _key === 'reason') {
								value = mapValue(value, eventID);
								if (value !== 'on' || value !== 'off') {
									obj[_key] = {
										name: _key,
										value,
										title: value,
									};
								} else {
									icon = eventLogIcon(_key, value, type);
								}
							}
							// sets icon when non change
							if (!icon) icon = eventLogIcon(_key, value, type);

							if (
								!/^(motion|vibration|tilt|position|reason|turn|state)$/.test(_key) ||
								(device.category !== 'inputs_reader' && _key == 'reason') ||
								(_key == 'state' && ['SHDW-1', 'SHDW-2'].includes(type))
							) {
								if (_key == 'alarm_hum' || _key == 'water') {
									_key = alarmHumToSensors(type);
								}
								if (_key == 'distance') {
									const distanceUnit =
										device?.ht_ui?.unit ||
										allSettings[real_id(device.id)]?.ht_ui?.unit ||
										'mm';
									let distanceValue = device?.measureFromBase
										? value + DISTANCE_BASE_SIZE
										: value;
									value = convertAndFormatDistance(
										distanceValue,
										distanceUnit,
										device.type,
									);
								} else if (_key == 'temperature') {
									const localUnit = tmp_units_from_settings(
										TEMPERATURE_SENSORS_IN_CELSIUS.includes(device.type)
											? allDevices[device.id]
											: allSettings[real_id(device.id)],
									);
									value =
										localUnit && (localUnit === 'F' || localUnit === '°F')
											? `${celsius_to_fahrenheit(value)} ${localUnit}`
											: `${value} ${localUnit}`;
								} else {
									value = mapValue(value, eventID);
									if (typeof value === 'string') {
										value = value.split(',')[0];
									}
									const translate = translateValue.find((i) => i.name == value);
									value = translate
										? get(format)(translate.i18n)
										: value + eventLabelsCharacters(_key, units);
								}

								// Add L/R suffix for vial_level when eventID is 21 (diffusers)
								if (eventID === 21 && _key === 'vial_level') {
									const channelSuffix =
										channel === 0 ? ' L' : channel === 1 ? ' R' : '';
									value = channelSuffix + ' ' + value;
								}

								let criticalIcon;
								if (eventID === 11) {
									if (index === 0) {
										return obj;
									} else if (index === 1) {
										const channel = Number(array[index - 1]);
										const criticalErrorDetected = get(format)(`${value}_detected`, {
											default: capFirstChar(value),
										});
										value =
											channel > 0
												? get(format)('event_critical_log', {
														default:
															'{criticalErrorDetected} for device channel {channel}',
														values: {
															criticalErrorDetected,
															channel: channel + 1,
														},
													})
												: criticalErrorDetected;
										criticalIcon = eventLogAdditionalIcon('critical', value);
									} else if (index === 2) {
										const criticalType = array[index - 1];
										let criticalUnit = '';
										switch (criticalType) {
											case 'overpower':
												criticalUnit = 'W';
												break;
											case 'overtemp':
												criticalUnit = units || '°C';
												break;
											case 'overvoltage':
											case 'undervoltage':
												criticalUnit = 'V';
												break;
										}
										value = `${value} ${criticalUnit}`;
									}
								}

								obj[_key] = {
									name: _key,
									value,
									icon: criticalIcon || eventLogAdditionalIcon(_key, value),
									...(_key === 'battery' && {
										iconStyle:
											'--fa-secondary-color: var(--color-3); --fa-secondary-opacity: 1;',
									}),
									type: eventID === 11 ? 'danger' : '',
								};
							}
						}
					}
					return obj;
				}, {});

				if (eventID === 11)
					icon = {
						icon: 'fas fa-shield',
						style: 'color: var(--color-danger);',
					};

				result.push({
					eventID,
					timestamp: value[i].t,
					catIcon: categoryIcon(category),
					roomID,
					name,
					data,
					room,
					category,
					icon: icon || { icon: '', style: '' },
					id: key,
				});
			}
		}
	}
	return sortOn(result, 'timestamp', true);
};

const eventLabelsCharacters = (key: string, units: string): string => {
	const labelMap: Record<string, string> = {
		battery: '%',
		humidity: '%',
		alarm_hum: '%', //humidity fot SBHT-003C
		position: '%',
		brightness: '%',
		vial_level: '%',
		temperature: units,
		tilt: '°',
		rotation: '°',
	};
	return labelMap[key] || '';
}; //Lowering the complexity of the code

const categoryIcon = (category: string): string => {
	const icons: Record<string, string> = {
		actuator: 'fas fa-microchip',
		sensor: 'fas fa-sensor-on',
		scene: 'fas fa-film',
		alarm: 'fas fa-siren-on',
		diffuser: 'fas fa-spray-can',
	};
	return icons[category] || icons['actuator']; //Lowering the complexity of the code
};

const eventLogIcon = (
	key: string,
	value: any,
	type: string,
): { icon: string | { value }; style: string } => {
	switch (key) {
		case 'vibration':
			return value
				? {
						icon: 'fas fa-waveform',
						style: 'color: var(--color-primary);',
					}
				: { icon: 'fas fa-dash', style: '' };
		case 'motion':
			return value
				? {
						icon: 'fas fa-person-walking',
						style: 'color: var(--color-primary);',
					}
				: { icon: 'fas fa-person', style: '' };
		case 'sensor':
			return { icon: 'fas fa-sensor-on', style: '' };
		case 'turn':
			return value
				? {
						icon: 'fas fa-toggle-on',
						style: 'color: var(--color-success);',
					}
				: {
						icon: 'fas fa-toggle-off',
						style: 'color: var(--color-danger);',
					};
		case 'open':
		case 'state':
			if (value == 'open') {
				return {
					icon: ['SHDW-1', 'SHDW-2'].includes(type) ? '' : 'fas fa-blinds-raised',
					style: '',
				};
			} else if (value == 'close') {
				return {
					icon: ['SHDW-1', 'SHDW-2'].includes(type) ? '' : 'fas fa-blinds',
					style: '',
				};
			} else if (value == 'stop') {
				return { icon: 'fas fa-pause-circle', style: '' };
			} else if (value == 'run') {
				return { icon: 'fas fa-film', style: '' };
			}
		case 'reason':
			if (value === 'off') {
				//added for input-readers
				return {
					icon: 'fas fa-toggle-off',
					style: 'color: var(--color-danger);',
				};
			} else if (value === 'on') {
				//added for input-readers
				return {
					icon: 'fas fa-toggle-on',
					style: 'color: var(--color-success);',
				};
			} else if (
				value !== 'on' &&
				value !== 'off' &&
				value !== 'periodic' &&
				typeof value !== 'boolean'
			) {
				//added for input-readers
				return { icon: { value }, style: '' }; //here we are passing input-reader events for custom icons
			} else if (value == 'periodic') {
				return { icon: 'fas fa-history', style: '' };
			}
			break;
		case 'smoke':
			return {
				icon: 'fa-solid fa-fire-smoke',
				style: 'color: var(--color-danger);',
			};
		case 'water':
			if (value) {
				return { icon: 'fas fa-house-flood-water text-danger', style: '' };
			} else return;
		case 'door':
			return {
				icon: `fas fa-door-${value ? 'open' : 'closed'}`,
				style: value ? 'color: var(--color-primary);' : '',
			};
		case 'lock':
			return value === 'open'
				? { icon: 'fas fa-door-open', style: 'color: var(--color-primary)' }
				: value === 'day_lock'
					? { icon: 'fas fa-unlock', style: 'color: var (--color-success);' }
					: { icon: 'fas fa-lock', style: 'color: var(--color-danger);' };
		default:
			return null;
	}
};

const eventLogAdditionalIcon = (key: string, value: string): string => {
	const iconMap: Record<string, string> = {
		distance: 'fas fa-ruler-horizontal',
		temperature: 'fas fa-thermometer',
		battery: 'fa-duotone fa-light fa-battery-full',
		humidity: 'fas fa-humidity',
		alarm_hum: 'fas fa-humidity',
		luxury: 'fas fa-sun',
		illumination: 'fas ' + getIlluminationIcon(value),
		motion: 'fas fa-person-walking',
		smoke: 'fa-solid fa-fire-smoke text-danger',
		brightness: 'fa-light fa-sun-bright',
		rotation: 'fas fa-ruler-triangle',
		vial_level: 'fas fa-spray-can',
		critical: 'fas fa-shield',
	};

	// Special case for water that depends on value
	if (key === 'water') {
		return value === get(format)('alarm_state_detected_yes')
			? 'fas fa-house-flood-water text-danger'
			: 'fas fa-droplet-slash';
	}

	// Special case for state that depends on value
	if (key === 'state') {
		return `fas fa-door-${value == 'open' ? 'open' : 'closed'}`;
	}

	return iconMap[key] || null;
};

const EVENT_SHORT_TO_LABELS = {
	tmp: 'temperature',
	bat: 'battery',
	alarm_hum: 'alarm_hum', //humidity for SHHT, but smoke and flood for Smoke and Water sensors
	current_pos: 'position',
	state: 'state',
	lux_value: 'luxury',
	lux_illumination: 'illumination',
	tilt: 'tilt',
	vibration: 'vibration',
	presence_detected: 'presence',
	presence_zone: 'presence_zone',
	illumination: 'illumination',
	motion: 'motion',
	smoke: 'smoke',
	water: 'water',
	humidity: 'humidity',
	brightness: 'brightness',
	rotation: 'rotation',
};

const COVER_CRITICAL_ERRORS = ['obstruction', 'safety_switch', 'bad_feedback', 'cal_abort'];

const EVENT_KEYS = {
	1: ['channel', 'turn'],
	2: ['channel'],
	3: ['tmp'],
	//4 is used for all wake up devices -> includes SHHT, SHSM, SHWT, shelly buttons and who knows what else
	4: ['reason', EVENT_SHORT_TO_LABELS.tmp, EVENT_SHORT_TO_LABELS.bat, EVENT_SHORT_TO_LABELS.alarm_hum],
	5: ['channel', 'state', EVENT_SHORT_TO_LABELS.current_pos],
	6: ['tstamp', 'rootname'],
	7: [
		'reason',
		'state',
		EVENT_SHORT_TO_LABELS.bat,
		EVENT_SHORT_TO_LABELS.lux_value,
		EVENT_SHORT_TO_LABELS.lux_illumination,
		'tilt',
		EVENT_SHORT_TO_LABELS.vibration,
		EVENT_SHORT_TO_LABELS.tmp,
	],
	8: ['channel', 'brightness'],
	9: ['alarm_gas'],
	10: [
		'motion',
		EVENT_SHORT_TO_LABELS.bat,
		EVENT_SHORT_TO_LABELS.lux_value,
		EVENT_SHORT_TO_LABELS.lux_illumination,
		EVENT_SHORT_TO_LABELS.tmp,
	],
	11: ['overpower', 'overtemp', 'overvoltage', 'undervoltage', ...COVER_CRITICAL_ERRORS],
	12: ['smoke'],
	13: ['channel', 'reason'], //inputs-reader event
	14: [
		undefined,
		'door',
		EVENT_SHORT_TO_LABELS.bat,
		EVENT_SHORT_TO_LABELS.lux_value,
		EVENT_SHORT_TO_LABELS.rotation,
	],
	15: ['motion', EVENT_SHORT_TO_LABELS.bat, EVENT_SHORT_TO_LABELS.lux_value],
	16: [
		EVENT_SHORT_TO_LABELS.tmp,
		EVENT_SHORT_TO_LABELS.alarm_hum,
		EVENT_SHORT_TO_LABELS.bat,
		EVENT_SHORT_TO_LABELS.lux_illumination, //Optional, SBHT-103C
	], //events for SBHT-003C
	17: ['module'], // events for module controllers and virtual components
	18: ['lock'], // events for LOQED devices
	19: [EVENT_SHORT_TO_LABELS.vibration, 'distance', EVENT_SHORT_TO_LABELS.bat],
	20: [
		'track',
		'no_track',
		EVENT_SHORT_TO_LABELS.presence_detected,
		EVENT_SHORT_TO_LABELS.presence_zone,
		EVENT_SHORT_TO_LABELS.illumination,
	],
	21: ['channel', 'turn', 'vial_level'], //events for diffusers
	22: ['camerazone', 'motion', 'recordingId', 'imageId'], // Camera motion events
	23: ['turn', 'reason'], // SPCB circuit breaker events
};

/** Camera events that include `data.recordingId` use custom expanded UI (thumbnail). */
export const eventLogHasRecordingExpand = (e: EventsLogData): boolean =>
	Boolean(e.data?.recordingId?.value);

const BREAKER_EVENT_SOURCE_LABELS: Record<string, string> = {
	init: 'event_log_source_init',
	local: 'event_log_source_local',
	HTTP_in: 'event_log_source_http_in',
	WS_in: 'event_log_source_ws_in',
	SHC: 'event_log_source_cloud',
	MQTT_in: 'event_log_source_mqtt',
	Schedule: 'event_log_source_schedule',
	Webhook: 'event_log_source_webhook',
};

const translateBreakerEventSource = (source: string): string => {
	const i18nKey = BREAKER_EVENT_SOURCE_LABELS[source];
	return i18nKey ? get(format)(i18nKey, { default: source }) : source;
};

const translateValue = [
	{ name: 'detected', i18n: 'alarm_state_detected_yes' },
	{ name: 'not detected', i18n: 'alarm_state_detected_no' },
	{ name: 'open', i18n: 'alarm_state_state_open' },
	{ name: 'close', i18n: 'alarm_state_state_close' },
	{ name: 'dark', i18n: 'alarm_state_dark' },
	{ name: 'bright', i18n: 'alarm_state_bright' },
	{ name: 'twilight', i18n: 'alarm_state_twilight' },
	{ name: 'smoke', i18n: 'alarm_state_smoke' },
	{ name: 'button', i18n: 'alarm_state_button' },
	{ name: 'sensor', i18n: 'alarm_state_sensor' },
	{ name: 'periodic', i18n: 'alarm_state_periodic' },
	{ name: 'none', i18n: 'alarm_state_none' },
	{ name: 'unknown', i18n: 'alarm_state_none' },
	{ name: 'day_lock', i18n: 'alarm_state_day_lock' },
	{ name: 'night_lock', i18n: 'alarm_state_night_lock' },
];

const alarmHumToSensors = (type: string) => {
	const sensorTypeMap: Record<string, string> = {
		'SHHT-1': EVENT_SHORT_TO_LABELS.humidity,
		'SNSN-0013A': EVENT_SHORT_TO_LABELS.humidity,
		'S3SN-0U12A': EVENT_SHORT_TO_LABELS.humidity,
		'S3SN-1U12A': EVENT_SHORT_TO_LABELS.humidity, //h&t g3 ffs
		'SHSM-01': EVENT_SHORT_TO_LABELS.smoke,
		'SNSN-0031Z': EVENT_SHORT_TO_LABELS.smoke, //Smoke Plus
		'S3SN-0082A': EVENT_SHORT_TO_LABELS.smoke, //Alarm bridge
		'S4SN-1031Z': EVENT_SHORT_TO_LABELS.smoke,
		'S4SN-0031Z': EVENT_SHORT_TO_LABELS.smoke,
		'SMSN-0031ZL': EVENT_SHORT_TO_LABELS.smoke,
		'SHWT-1': EVENT_SHORT_TO_LABELS.water,
		'S4SN-0071A': EVENT_SHORT_TO_LABELS.water, //flood gen 4
		'S4SN-0071Z': EVENT_SHORT_TO_LABELS.water, //flood S gen 4
	};

	return sensorTypeMap[type] || EVENT_SHORT_TO_LABELS.alarm_hum;
}; //Lowering the complexity of the code

const mapValue = (value: any, eventId: number) => {
	if (typeof value == 'boolean') {
		const booleanValueMap: Record<number, string> = {
			1: 'on',
			13: 'on',
			23: 'on',
			4: 'detected',
			10: 'motion',
		};

		const falseValueMap: Record<number, string> = {
			1: 'off',
			13: 'off',
			23: 'off',
			4: 'not detected',
			10: 'no motion',
		};

		return value ? booleanValueMap[eventId] || 'unknown' : falseValueMap[eventId] || 'unknown';
	} else {
		return eventId === 12 ? 'smoke' : value;
	}
}; //Lowering the complexity of the code

type EventTableHeadingsType = {
	[key: string]: {
		id: string;
		title?: string;
		sort?: boolean;
		desc?: boolean;
		icon?: string;
	};
};

export const getEventTableHeadings = (): EventTableHeadingsType => {
	return {
		deviceIcon: {
			id: 'deviceIcon',
			icon: 'fas fa-filter',
		},
		timestamp: {
			id: 'timestamp',
			title: get(format)('timestamp', { default: 'timestamp' }),
			sort: true,
			desc: true,
		},
		room: {
			id: 'room',
			title: get(format)('process_room', { default: 'room' }),
			sort: true,
			desc: true,
		},
		name: {
			id: 'name',
			title: get(format)('name', { default: 'name' }),
			sort: true,
			desc: true,
		}, // TODO: /add data to all translation files,
		info: {
			id: 'info',
			title: get(format)('short_info', { default: 'info' }),
			sort: false,
			desc: true,
			icon: 'fas fa-circle-info',
		},
	};
};

/**
 * Get device tags out of the provided devices
 * Returns array with tags
 */
const getAllDevicesTags = (allDevices: { [key: string]: Device }) => {
	let tags = [];
	for (const device_id in allDevices) {
		let device = allDevices[device_id];
		if (
			device?.shared ||
			isVirtualDevice(device?.type) ||
			isBluTRV(device?.type) ||
			isPeripheralDevice(device?.type)
		) {
			continue; //Skip shared and Virtual devices
		}
		tags.push(device_id);

		//Device-specific tags
		if (isPill(device.type)) {
			tags.push(...getThePillEventLogTags(device));
		} else if (device.channel == 0 && isProSensorAddon(device)) {
			tags = [...tags, ...getSwitchPeripheralsTags(device_id)];
		}
	}
	return tags;
};

export const getTagsAndDeviceChannel = (
	device: Device,
	devicesArr: Device[] | [],
): [string[], number] => {
	const allDevices = get(devices);
	const allScene = get(scenes);
	const allDevicesArr = Object.values(allDevices);

	let deviceID = device?.id;
	let deviceChannel = -1;
	let tags = [];

	if (device == null && devicesArr.length === 0) {
		tags = [...getAllDevicesTags(allDevices), ...Object.keys(allScene)]; //removing shared devices from tags
	} else if (devicesArr.length > 0) {
		devicesArr.forEach((dev: Device) => {
			let split = dev?.id?.split('_') || [];
			if (split.length > 0) {
				if (!tags.includes(deviceID)) {
					tags.push(dev?.id);
				}
			}
		});
	} else {
		const splitDeviceID = deviceID.toString().split('_');
		deviceID = splitDeviceID[0];
		deviceChannel = Number(splitDeviceID[1]) || 0;
		const realDeviceID = real_id(device.id);
		if (isVirtualDevice(device.type)) {
			tags = [realDeviceID];
		} else if (isPill(device.type)) {
			tags = getThePillEventLogTags(device);
		} else if (isPeripheralDevice(device.type)) {
			tags = [`${realDeviceID}_${device.component.split(':')[1]}`];
		} else if (isBreaker(device.type)) {
			tags = [realDeviceID];
		} else {
			tags = isEM(device.type) ? [realDeviceID] : [device.id];
			tags = [...tags, ...getSwitchPeripheralsTags(realDeviceID, true)];
		}
	}
	return [tags, deviceChannel];
};

export const getEvents = async (
	loadMore: boolean = false,
	pointer: string,
	tags: string[],
	limit: number,
) => {
	const payload: EventsLogRequest = loadMore && pointer ? { pointer } : { tags },
		res: EventsLogResponse =
			pointer || tags.length > 0
				? await postReq('/statistics/event-log', { ...payload, limit }, 20000, '', true, true)
				: undefined; //preventing request if there are no valid pointer or tags

	return res;
};

const handleCameraZoneEvent = (allSettings: any, device: Device, value: any, key: string) => {
	const settings = allSettings[real_id(device.id)];
	const zone = getCameraZone(settings, Number(value));
	const zoneName = zone?.name || `Zone ${value}`;
	const zoneColor = zone?.color ? rgbToCss(zone?.color, 1) : 'text-primary';

	return {
		name: key,
		value: zoneName,
		icon: 'fa-duotone fa-light fa-circle',
		iconStyle: `--fa-primary-color: var(--color-white); --fa-secondary-color: ${zoneColor}; --fa-secondary-opacity: 1`,
	};
};
